<template>
  <div class="modal-card" style="width: auto">
    <b-loading
      :is-full-page="true"
      :active="isLoading"
      :can-cancel="false"
    ></b-loading>

    <header class="modal-card-head">
      <p class="modal-card-title has-text-centered">
        {{
          this.bagBeingEdited._id ? $t("bags.editBag") : $t("bags.createBag")
        }}
      </p>
    </header>

    <section class="modal-card-body">
      <h2 class="subtitle">{{ $t("bags.bagInfo") }}</h2>

      <div class="columns">
        <div class="column">
          <b-field :label="$t('bags.name')" label-position="on-border">
            <b-input v-model="bag.name"></b-input>
          </b-field>
        </div>
      </div>

      <div class="columns">
        <div class="column is-narrow">
          <b-field
            :label="$t('bags.installmentsUpTo')"
            label-position="on-border"
          >
            <b-select v-model="bag.maxInstallments">
              <option
                v-for="installment in installments"
                :key="installment.value"
                :value="installment.value"
                >{{ installment.description }}</option
              >
            </b-select>
          </b-field>
        </div>
        <div class="column is-narrow">
          <b-field :label="$t('bags.terminalModel')" label-position="on-border">
            <b-select v-model="bag.terminal.model">
              <option value=""></option>
              <option value="P400Plus">P400Plus</option>
              <option value="V240mPlus">V240mPlus</option>
              <option value="S1F2">S1F2</option>
            </b-select>
          </b-field>
        </div>
        <div class="column is-narrow">
          <b-field
            :label="$t('bags.terminalSerialNumber')"
            label-position="on-border"
          >
            <b-input v-model="bag.terminal.serialNumber"></b-input>
          </b-field>
        </div>
      </div>
      <br />
      <h2 class="subtitle" v-if="bagBeingEdited._id">
        {{ $t("bags.products") }}
        <button
          class="button float-left is-small ml-2"
          @click="$refs.bulkAddProducts.click()"
          style="display:inline; position: relative; bottom: 2px;"
        >
          <b-icon icon="file-upload"></b-icon>
        </button>
      </h2>
      <bag-products-table
        v-if="bagBeingEdited._id"
        :products="bagBeingEdited.products"
        :currencySymbol="storeBeingManaged.payments.currencySymbol"
      />

      <input
        type="file"
        ref="bulkAddProducts"
        id="bulk-add-bag-products"
        style="display: none"
        accept=".xls,.xlsx"
        @change="bulkAddProducts"
      />
    </section>

    <footer class="modal-card-foot columns">
      <div class="column has-text-centered" style="padding: 0; margin: 0;">
        <button class="button" type="button" @click="$parent.close()">
          {{ $t("common.cancel") }}
        </button>
      </div>

      <div class="column has-text-centered">
        <button
          class="button"
          :style="{
            backgroundColor: $primaryColor,
            color: $primaryColorContrast
          }"
          @click="createUpdateBag"
          :disabled="!canSave"
        >
          {{ this.bagBeingEdited._id ? $t("bags.update") : $t("bags.create") }}
        </button>
      </div>
    </footer>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { common } from "../../mixins/common";
import bagProductsTable from "./BagsProductsTable.vue";

export default {
  props: ["bagBeingEdited"],

  mixins: [common],

  components: {
    bagProductsTable
  },

  data() {
    return {
      isLoading: false,
      bag: {
        name: "",
        uuid: "",
        maxInstallments: "1",
        products: [],
        terminal: {
          model: "",
          serialNumber: ""
        }
      }
    };
  },

  methods: {
    createUpdateBag() {
      if (this.bagBeingEdited._id) {
        this.updateBag();
      } else {
        this.createBag();
      }
    },

    createBag() {
      this.isLoading = true;
      this.$store
        .dispatch("createBag", {
          bag: this.bag,
          storeId: this.storeBeingManaged._id
        })
        .then(res => {
          if (res.data.result === "ok") {
            this.$emit("on-bag-added", res.data.payload);
            this.$parent.close();
          } else {
            this.alertError(res.data);
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    updateBag() {
      this.isLoading = true;
      this.$store
        .dispatch("updateBag", {
          bag: this.bag,
          storeId: this.storeBeingManaged._id
        })
        .then(res => {
          if (res.data.result === "ok") {
            this.$emit("on-bag-updated", res.data.payload.bag);
            this.$parent.close();
          } else {
            this.alertError(res.data);
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    bulkAddProducts(event) {
      const formData = new FormData();
      const files = event.target.files;

      if (
        files[0].type !== "xls" &&
        files[0].type !== "xlsx" &&
        files[0].type !==
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ) {
        this.alertError({ message: "FILE_EXTENSION_NOT_ALLOWED_XLS_XLSX" });
        return;
      }

      if (files[0].size > 100000) {
        this.alertError({ message: "FILE_IS_TOO_LARGE_100" });
        return;
      }

      formData.append("file", files[0]);
      formData.append("storeId", this.storeBeingManaged._id);
      formData.append("bagId", this.bag._id);

      this.isLoading = true;
      this.$store
        .dispatch("bulkAddBagProducts", formData)
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.bagBeingEdited.products = res.data.payload.products;
            this.$emit("on-products-updated", res.data.payload.products);
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
          //this.$emit("processing-purchases");
          document.getElementById("bulk-add-bag-products").value = "";
        });
    },

    getBag() {
      this.isLoading = true;
      this.$store
        .dispatch("getBag", {
          bagId: this.bag._id,
          storeId: this.storeBeingManaged._id
        })
        .then(res => {
          console.log("Get BAG", this.bagBeingEdited);
          if (res.data.result === "ok") {
            this.bagBeingEdited.products = res.data.payload.bag.products;
          } else {
            this.alertError(res.data);
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  },

  computed: {
    ...mapGetters(["storeBeingManaged"]),
    canSave() {
      return this.bag.name && this.bag.name.trim() !== "";
    },
    installments() {
      let p = [];

      for (let i = 1; i <= 12; i++) {
        if (i == 1) {
          p.push({ value: i.toString(), description: this.$t("bags.onCache") });
        } else {
          p.push({
            value: i,
            description: `${i.toString()} ${this.$t("bags.installments")}`
          });
        }
      }

      return p;
    }
  },

  mounted() {
    if (this.bagBeingEdited._id) {
      this.bag = this.bagBeingEdited;
      if (!this.bag.terminal) {
        this.bag.terminal = {};
      }

      if (!this.bag.terminal.model) this.bag.terminal.model = "";

      if (!this.bag.terminal.serialNumber) this.bag.terminal.serialNumber;

      this.getBag();
    }
  }
};
</script>

<style scoped>
.switch-adjusted-top {
  position: relative;
  top: 2px;
}

.top-permission-p {
  border-top: 1px solid lightgrey;
  border-bottom: 1px solid lightgrey;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.bottom-permission-p {
  border-bottom: 1px solid lightgrey;
  padding-bottom: 0.5rem;
  padding-top: 0.5rem;
}
</style>
